import { createClient } from '@supabase/supabase-js';

// Get Supabase credentials from environment variables (injected at build time by Vite)
const explicit = (import.meta.env.VITE_SUPABASE_URL as string | undefined)?.trim().replace(/\/$/, '') || '';
const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID || '';
const publicAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';

if (!publicAnonKey) {
  throw new Error(
    'VITE_SUPABASE_ANON_KEY is missing. Set it in .env (local) or Vercel → Project → Settings → Environment Variables, then rebuild.'
  );
}

// Prefer the explicit URL (custom domain like https://api.upstand.ai); fall back
// to constructing the default project URL from VITE_SUPABASE_PROJECT_ID. The
// exported `supabaseUrl` is the canonical base for any code that needs to build
// edge function URLs by hand — don't construct https://${projectId}.supabase.co
// inline anywhere; import this instead.
export const supabaseUrl = explicit || (projectId ? `https://${projectId}.supabase.co` : '');

if (!supabaseUrl) {
  throw new Error(
    'Set VITE_SUPABASE_URL (preferred) or VITE_SUPABASE_PROJECT_ID in .env / Vercel env vars.'
  );
}

/** Build an Edge Function URL: `${supabaseUrl}/functions/v1/${name}`. */
export function edgeFnUrl(name: string): string {
  return `${supabaseUrl}/functions/v1/${name}`;
}

// Create a single supabase client for interacting with your database
export const supabase = createClient(supabaseUrl, publicAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true, // CRITICAL: This makes Supabase detect OAuth tokens in URL
    flowType: 'pkce', // Use PKCE flow for better security
  },
});

// Helper function to get current user
export async function getCurrentUser() {
  const {
    data: { user },
    error,
  } = await supabase.auth.getUser();
  if (error) throw error;
  return user;
}

